import { AxiosHeaders, isAxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios'
import type { ApiSession } from '../lib/browserSession'
import { readApiProblem } from './problem'

type SessionRequest = InternalAxiosRequestConfig & { _nonceRetried?: boolean; _refreshRetried?: boolean; _dpopNonce?: string }

export function requestTarget(instance: AxiosInstance, config: InternalAxiosRequestConfig): URL {
  const base = new URL(instance.defaults.baseURL ?? '/', window.location.origin)
  const target = new URL(instance.getUri(config), window.location.origin)
  const prefix = base.pathname.replace(/\/$/, '')
  if (target.origin !== base.origin || !(target.pathname === prefix || target.pathname.startsWith(`${prefix}/`))) {
    throw new Error('Refusing to send browser credentials outside the configured API')
  }
  return target
}

function hasChallenge(headers: AxiosHeaders, code: string): boolean {
  const challenge = String(headers.get('www-authenticate') ?? '')
  return /^DPoP(?:\s|$)/i.test(challenge) && new RegExp(`(?:^|[\\s,])error="${code}"(?:[,\\s]|$)`).test(challenge)
}

function canRefreshAndReplay(config: InternalAxiosRequestConfig): boolean {
  if (['GET', 'HEAD', 'OPTIONS'].includes((config.method ?? 'get').toUpperCase())) return true
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/.test(String(config.headers.get('Idempotency-Key') ?? ''))
}

export function installSessionInterceptors(instance: AxiosInstance, getSession: () => ApiSession | undefined): void {
  instance.interceptors.request.use(async raw => {
    const config = raw as SessionRequest
    const target = requestTarget(instance, config)
    config.headers.delete('Authorization')
    config.headers.delete('DPoP')
    if (config.data instanceof FormData) config.headers.delete('Content-Type')
    const credentials = await getSession()?.authorize(target.href, config.method ?? 'GET', config._dpopNonce)
    if (credentials) {
      config.headers.set('Authorization', credentials.authorization)
      config.headers.set('DPoP', credentials.proof)
    }
    return config
  })
  instance.interceptors.response.use(response => response, async (error: unknown) => {
    if (!isAxiosError(error) || !error.config || error.response?.status !== 401) return Promise.reject(error)
    const original = error.config as SessionRequest
    const problem = readApiProblem(error)
    const session = getSession()
    if (!session || problem?.code !== 'BDI-HTTP-401') return Promise.reject(error)
    const headers = new AxiosHeaders()
    for (const [name, value] of Object.entries(error.response.headers)) {
      if (typeof value === 'string') headers.set(name, value)
    }
    const nonce = headers.get('DPoP-Nonce')
    if (!original._nonceRetried && typeof nonce === 'string' && /^[\x21-\x7e]{1,512}$/.test(nonce)
        && hasChallenge(headers, 'use_dpop_nonce')) {
      original._nonceRetried = true
      original._dpopNonce = nonce
      return instance(original)
    }
    if (!original._refreshRetried && hasChallenge(headers, 'invalid_token') && canRefreshAndReplay(original)) {
      original._refreshRetried = true
      if (await session.refresh(String(original.headers.get('Authorization') ?? ''))) return instance(original)
    }
    // Transport errors, unknown 401s and unkeyed commands never cause a replay or global navigation.
    return Promise.reject(error)
  })
}
